import React, { useState, useEffect, lazy, Suspense, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import './App.css';
import './appglobals.css';

import { setAuthToken, apiClient } from './api/client';
import { useClickUpCacheGuard } from './hooks/useClickUpCacheGuard';
import { isAdminOrOwner } from './utils/userRoles';

import Login from './components/Login/Login';
import Register from './components/Register/Register';
import DataHydrator from './components/DataHydrator/DataHydrator';
import { ThemeProvider } from './context/ThemeContext';
import CommandPalette, { type OpenClawPageContext } from './components/CommandPalette/CommandPalette';
import BackgroundManager from './components/BackgroundManager/BackgroundManager';
import AppLayout from './components/layout/AppLayout';
import SiteNotification from './components/SiteNotification/SiteNotification';
import Spinner from './components/Spinner';
import GlobalErrorBoundary from './components/common/GlobalErrorBoundary/GlobalErrorBoundary';
import ClientLoader from './client/components/ClientLoader/ClientLoader';
import ContractGeneratorModal from './components/Contracts/ContractGeneratorModal';
import ClickUpModal from './components/ClickUp/ClickUpModal';
import SitePet from './components/SitePet/SitePet';

const CampaignDetails = lazy(() => import('./components/CampaignDetails/CampaignDetails'));
const AnalyticsTablePage = lazy(() => import('./components/AnalyticsTablePage/AnalyticsTablePage'));
const AdminPanel = lazy(() => import('./components/AdminPanel/AdminPanel'));
const RealtimeDashboard = lazy(() => import('./components/RealtimeDashboard/RealtimeDashboard'));
const SpecialOffersDashboard = lazy(() => import('./components/SpecialOffersDashboard/SpecialOffersDashboard'));
const FeedbackPage = lazy(() => import('./components/Feedback/FeedbackPage'));
const AutoloadPage = lazy(() => import('./components/AutoloadPage'));
const AutoloadReportDetailsPage = lazy(() => import('./components/AutoloadReportDetailsPage'));
const ProfileSettings = lazy(() => import('./components/ProfileSettings/ProfileSettings'));
const SpecialOffersCalculator = lazy(() => import('./components/SpecialOffersCalculator/SpecialOffersCalculator'));
const RegistryPage = lazy(() => import('./components/RegistryPage/RegistryPage'));
const ParserPage = lazy(() => import('./components/ParserPage/ParserPage'));
const PhotoFlowPage = lazy(() => import('./components/PhotoFlowPage/PhotoFlowPage'));
const CallsCalendarPage = lazy(() => import('./components/CallsCalendar/CallsCalendarPage'));
const CallsCalendarMockPage = lazy(() => import('./components/CallsCalendar/CallsCalendarMockPage'));
const HomePage = lazy(() => import('./components/HomePage/HomePage'));
const MonitoringPage = lazy(() => import('./components/MonitoringPage/MonitoringPage'));
const NotFound = lazy(() => import('./components/NotFound/NotFound'));
const ReviewsDashboardPage = lazy(() => import('./components/ReviewsDashboard/ReviewsDashboardPage'));
const SummariesPage = lazy(() => import('./components/SummariesPage/SummariesPage'));
const IdeasPage = lazy(() => import('./components/Ideas/IdeasPage'));
const IdeasAdmin = lazy(() => import('./components/Ideas/IdeasAdmin'));
const ClientPortal = lazy(() => import('./client/ClientPortal'));
const ClientHomePage = lazy(() => import('./client/pages/ClientHomePage/ClientHomePage'));
const SummarySharePage = lazy(() => import('./client/pages/SummarySharePage/SummarySharePage'));
const SummaryViewPage = lazy(() => import('./client/pages/SummaryViewPage/SummaryViewPage'));
const InboxPage = lazy(() => import('./components/InboxPage/InboxPage'));
const AccountCabinet = lazy(() => import('./components/AccountCabinet/AccountCabinet'));

function AppGuards() {
  useClickUpCacheGuard();
  return null;
}

// ClickUp Route Trigger - открывает модалку при переходе на /clickup/*
function ClickUpRouteTrigger({ view, taskId }: { view: 'inbox' | 'tasks' | 'task-detail'; taskId?: string }) {
  const navigate = useNavigate();

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('open-clickup', {
      detail: { view, taskId }
    }));
    // Возвращаемся назад в истории, чтобы URL не менялся
    window.history.back();
  }, [view, taskId]);

  return null;
}

const isCabinetMock = process.env.REACT_APP_CABINET_MOCK === 'true';

function App() {
  const isCallsCalendarMockPreview = window.location.search.includes('preview=calls-calendar-mock');
  const [authToken, setAuthTokenState] = useState<string | null>(isCabinetMock ? 'cabinet-mock' : localStorage.getItem('authToken'));
  const [currentPage, setCurrentPage] = useState<'login' | 'register'>('login');
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [paletteContext, setPaletteContext] = useState<OpenClawPageContext | null>(null);
  const [recentClientErrors, setRecentClientErrors] = useState<string[]>([]);
  const reportedClientErrorsRef = useRef<Map<string, number>>(new Map());
  const [isAdminPanelOpen, setIsAdminPanelOpen] = useState(false);
  const [isContractGeneratorOpen, setIsContractGeneratorOpen] = useState(false);
  const [clickUpModal, setClickUpModal] = useState<{
    isOpen: boolean;
    view: 'inbox' | 'tasks' | 'task-detail';
    taskId?: string;
  }>({
    isOpen: false,
    view: 'inbox',
  });
  const [currentUser, setCurrentUser] = useState<any | null>(() => {
    if (isCabinetMock) return { id: 'cabinet-mock', username: 'Дизайн кабинетов', role: 'owner' };
    const storedUser = localStorage.getItem('currentUser');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  useEffect(() => {
    if (isCabinetMock) {
      setAuthToken('cabinet-mock');
      setCurrentUser({ id: 'cabinet-mock', username: 'Дизайн кабинетов', role: 'owner' });
    } else if (authToken) {
      setAuthToken(authToken);
      loadCurrentUser();
    } else {
      setCurrentUser(null);
    }

    const handleUserUpdate = () => {
      loadCurrentUser();
    };

    window.addEventListener('user-updated', handleUserUpdate);
    return () => window.removeEventListener('user-updated', handleUserUpdate);
  }, [authToken]);

  useEffect(() => {
    const collectErrorPageContext = () => {
      const headings = Array.from(document.querySelectorAll('h1, h2, [data-ai-title]'))
        .map((node) => (node.textContent || '').trim())
        .filter(Boolean)
        .slice(0, 8);
      const visibleErrors = Array.from(document.querySelectorAll('[role="alert"], .error, .alert, .toast, .notification, [class*="error"], [class*="Error"]'))
        .map((node) => (node.textContent || '').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .slice(0, 8);
      const pageText = (document.querySelector('main')?.textContent || document.body?.innerText || '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 2500);
      return {
        title: document.title,
        path: `${window.location.pathname}${window.location.search}`,
        headings,
        visibleErrors,
        pageText,
      };
    };

    const reportClientError = (data: { message: string; errorType?: string; stack?: string; recentErrors: string[] }) => {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        return;
      }

      const key = `${data.errorType || 'Error'}:${data.message}`.slice(0, 500);
      const now = Date.now();
      const lastSentAt = reportedClientErrorsRef.current.get(key) || 0;
      if (now - lastSentAt < 30_000) {
        return;
      }
      reportedClientErrorsRef.current.set(key, now);

      fetch('/api/v1/client-errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: data.message,
          error_type: data.errorType,
          stack: data.stack,
          url: window.location.href,
          route: `${window.location.pathname}${window.location.search}`,
          user_agent: navigator.userAgent,
          severity: 'frontend_runtime',
          recent_errors: data.recentErrors,
          page_context: collectErrorPageContext(),
        }),
      }).catch(() => undefined);
    };

    const pushError = (text: string, errorType?: string, stack?: string) => {
      const normalized = String(text || '').trim();
      if (!normalized) {
        return;
      }
      setRecentClientErrors((prev) => {
        const nextErrors = [normalized, ...prev.filter((item) => item !== normalized)].slice(0, 8);
        reportClientError({ message: normalized, errorType, stack, recentErrors: nextErrors });
        return nextErrors;
      });
    };

    const handleError = (event: ErrorEvent) => {
      pushError(
        `${event.message || 'Runtime error'}${event.filename ? ` (${event.filename}:${event.lineno || 0})` : ''}`,
        event.error?.name || 'ErrorEvent',
        event.error?.stack,
      );
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      pushError(
        reason?.message || reason?.detail || String(reason || 'Unhandled promise rejection'),
        reason?.name || 'UnhandledPromiseRejection',
        reason?.stack,
      );
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  const collectPaletteContext = useCallback((intent: OpenClawPageContext['intent'] = 'page'): OpenClawPageContext => {
    const selectedText = String(window.getSelection?.()?.toString() || '').trim().slice(0, 4000);
    const headings = Array.from(document.querySelectorAll('h1, h2, [data-ai-title]'))
      .map((node) => (node.textContent || '').trim())
      .filter(Boolean)
      .slice(0, 10);

    const visibleErrors = Array.from(document.querySelectorAll('[role="alert"], .error, .alert, .toast, .notification, [class*="error"], [class*="Error"]'))
      .filter((node) => {
        const element = node as HTMLElement;
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
      })
      .map((node) => (node.textContent || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .slice(0, 8);

    const actions = Array.from(document.querySelectorAll('button, a[href], input, select, textarea, [role="button"], [role="tab"], [role="menuitem"]'))
      .filter((node) => {
        const element = node as HTMLElement;
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);
        return rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && style.display !== 'none';
      })
      .map((node) => {
        const element = node as HTMLElement;
        const label =
          element.getAttribute('aria-label') ||
          element.getAttribute('title') ||
          element.textContent ||
          element.getAttribute('placeholder') ||
          element.getAttribute('href') ||
          '';
        const tag = element.tagName.toLowerCase();
        return `${tag}: ${label.replace(/\s+/g, ' ').trim()}`;
      })
      .filter((item) => item.length > 3)
      .slice(0, 40);

    const mainText = (document.querySelector('main')?.textContent || document.body?.innerText || '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 3500);

    return {
      intent: selectedText ? 'selection' : intent,
      url: window.location.href,
      path: `${window.location.pathname}${window.location.search}`,
      title: document.title,
      selectedText: selectedText || undefined,
      headings,
      visibleErrors,
      recentErrors: recentClientErrors,
      actions,
      pageText: mainText,
      generatedAt: new Date().toISOString(),
    };
  }, [recentClientErrors]);

  const openPaletteWithContext = useCallback((intent: OpenClawPageContext['intent'] = 'page') => {
    setPaletteContext(collectPaletteContext(intent));
    setIsPaletteOpen(true);
  }, [collectPaletteContext]);

  const loadCurrentUser = async () => {
    try {
      const user = await apiClient.getCurrentUser();
      setCurrentUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
    } catch (err) {
      console.error('Failed to load current user:', err);
      setCurrentUser(null);
      localStorage.removeItem('currentUser');
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
        event.preventDefault();
        if (isPaletteOpen) {
          setIsPaletteOpen(false);
          return;
        }
        openPaletteWithContext(event.shiftKey ? 'error' : 'page');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPaletteOpen, openPaletteWithContext]);

  // ClickUp modal event listener
  const handleOpenClickUp = useCallback((e: Event) => {
    const customEvent = e as CustomEvent;
    const { view, taskId } = customEvent.detail || {};
    setClickUpModal({
      isOpen: true,
      view: view || 'inbox',
      taskId,
    });
  }, []);

  const handleCloseClickUp = useCallback(() => {
    setClickUpModal({ isOpen: false, view: 'inbox' });
  }, []);

  useEffect(() => {
    window.addEventListener('open-clickup', handleOpenClickUp);
    return () => window.removeEventListener('open-clickup', handleOpenClickUp);
  }, [handleOpenClickUp]);

  const handleLoginSuccess = async (token: string) => {
    localStorage.setItem('authToken', token);
    setAuthToken(token);
    setAuthTokenState(token);

    try {
      const user = await apiClient.getCurrentUser();
      setCurrentUser(user);
      localStorage.setItem('currentUser', JSON.stringify(user));
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentUser');
    setAuthToken(null);
    setAuthTokenState(null);
    setCurrentUser(null);
  };

  const portalFallback = (
    <ClientLoader label="Загрузка данных" />
  );

  const mainAppFallback = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        color: 'var(--text-secondary)',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        <Spinner />
        <p>Загрузка...</p>
      </div>
    </div>
  );

  const mainAppContent = !authToken ? (
    <ThemeProvider>
      {currentPage === 'login' ? (
        <Login
          onLoginSuccess={handleLoginSuccess}
          onNavigateToRegister={() => setCurrentPage('register')}
        />
      ) : (
        <Register
          onRegisterSuccess={handleLoginSuccess}
          onBackToLogin={() => setCurrentPage('login')}
        />
      )}
    </ThemeProvider>
  ) : (
    <ThemeProvider>
      {isPaletteOpen && <CommandPalette onClose={() => setIsPaletteOpen(false)} initialContext={paletteContext} currentUser={currentUser} />}
      <AppGuards />
      <DataHydrator>
        <BackgroundManager />
        <SiteNotification />
        <SitePet />
        {isAdminOrOwner(currentUser) && (
          <AdminPanel
            isOpen={isAdminPanelOpen}
            onClose={() => setIsAdminPanelOpen(false)}
          />
        )}
        <AppLayout
          currentUser={currentUser}
          onOpenPalette={() => openPaletteWithContext('page')}
          onOpenContractsModal={() => setIsContractGeneratorOpen(true)}
          onOpenAdminPanel={() => setIsAdminPanelOpen(true)}
        >
          <Suspense fallback={mainAppFallback}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/analytics" element={<AnalyticsTablePage />} />
              <Route path="/analytics/:accountName" element={<AnalyticsTablePage />} />
              <Route path="/summaries" element={<SummariesPage />} />
              <Route path="/cabinet" element={<AccountCabinet />} />
              <Route path="/cabinet/:accountName" element={<AccountCabinet />} />
              <Route path="/registry" element={<RegistryPage currentUser={currentUser} />} />
              <Route path="/dashboard" element={<Navigate to="/" replace />} />
              <Route path="/special-offers" element={<SpecialOffersCalculator />} />
              <Route path="/special-offers/dashboard" element={<SpecialOffersDashboard />} />
              <Route path="/realtime-dashboard" element={<RealtimeDashboard />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/reviews" element={<ReviewsDashboardPage />} />
              <Route path="/autoload" element={<AutoloadPage />} />
              <Route path="/monitoring" element={<MonitoringPage currentUser={currentUser} />} />
              <Route path="/parser" element={<ParserPage />} />
              <Route path="/photoflow" element={<PhotoFlowPage />} />
              <Route path="/calls" element={<CallsCalendarPage />} />
              <Route path="/ideas" element={<IdeasPage />} />
              <Route path="/ideas/:id" element={<IdeasPage />} />
              <Route path="/admin/ideas" element={<IdeasAdmin />} />
              <Route path="/clickup" element={<ClickUpRouteTrigger view="inbox" />} />
              <Route path="/clickup/inbox" element={<ClickUpRouteTrigger view="inbox" />} />
              <Route path="/clickup/tasks" element={<ClickUpRouteTrigger view="tasks" />} />
              <Route path="/clickup/task/:taskId" element={<ClickUpRouteTrigger view="task-detail" />} />
              <Route
                path="/autoload/report/:accountName/:reportId"
                element={<AutoloadReportDetailsPage />}
              />
              <Route path="/campaign/:accountName" element={<CampaignDetails />} />
              <Route path="/comparison" element={<Navigate to="/" replace />} />
              <Route path="/inbox" element={<InboxPage />} />
              <Route path="/profile" element={<ProfileSettings onLogout={handleLogout} />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AppLayout>
        <ContractGeneratorModal
          open={isContractGeneratorOpen}
          onClose={() => setIsContractGeneratorOpen(false)}
        />
        <ClickUpModal
          isOpen={clickUpModal.isOpen}
          onClose={handleCloseClickUp}
          initialView={clickUpModal.view}
          initialTaskId={clickUpModal.taskId}
        />
      </DataHydrator>
    </ThemeProvider>
  );

  const callsCalendarMockPreview = (
    <ThemeProvider>
      <Suspense fallback={mainAppFallback}>
        <CallsCalendarMockPage />
      </Suspense>
    </ThemeProvider>
  );

  return (
    <Provider store={store}>
      <GlobalErrorBoundary>
        <Router>
          <Routes>
            <Route
              path="/share/:token"
              element={
                <Suspense fallback={portalFallback}>
                  <ClientPortal>
                    <SummarySharePage />
                  </ClientPortal>
                </Suspense>
              }
            />
            <Route
              path="/view/:token"
              element={
                <Suspense fallback={portalFallback}>
                  <ClientPortal>
                    <SummaryViewPage />
                  </ClientPortal>
                </Suspense>
              }
            />
            <Route
              path="/client"
              element={
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem', fontFamily: 'system-ui, sans-serif', textAlign: 'center', background: '#f8f9fa' }}>
                  <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#333' }}>Клиентский кабинет перенесён в Telegram</h1>
                  <p style={{ color: '#666', maxWidth: '400px', lineHeight: 1.6 }}>
                    Все отчёты, позиции и парсер теперь доступны прямо в Telegram-боте.<br />
                    Откройте бота и введите <b>/menu</b> для доступа к кабинету.
                  </p>
                </div>
              }
            />
            <Route path="/client/login" element={<Navigate to="/client" replace />} />
            <Route path="/client/*" element={<Navigate to="/client" replace />} />
            <Route path="/" element={isCallsCalendarMockPreview ? callsCalendarMockPreview : mainAppContent} />
            <Route
              path="/calls-calendar-mock"
              element={callsCalendarMockPreview}
            />
            <Route path="*" element={mainAppContent} />
          </Routes>
        </Router>
      </GlobalErrorBoundary>
    </Provider>
  );
}

export default App;
